"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useTrackEvent } from "@/hooks/useTrackEvent";

const lessonVideoSrc = "/videos/local-lessons/liuba-marisa-coaching-lesson.mp4";
const lessonPosterSrc = "/videos/local-lessons/liuba-marisa-coaching-lesson-poster.jpg";

export function AuthorityLessonVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const figureRef = useRef<HTMLElement>(null);
  const seen = useRef(new Set<string>());
  const [isPlaying, setIsPlaying] = useState(false);
  const trackEvent = useTrackEvent();

  const record = (event: string) => {
    if (seen.current.has(event)) return;
    seen.current.add(event);
    trackEvent(event, { video_id: "marisa-lesson", video_placement: "home", source_page: "/" });
  };

  useEffect(() => {
    const element = figureRef.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        record("lesson_video_impression");
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => observer.disconnect();
    // Record one impression for this video instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playVideo = () => {
    setIsPlaying(true);
    void videoRef.current?.play();
  };

  return (
    <figure ref={figureRef} className="relative min-h-[22rem] overflow-hidden rounded-[18px] bg-[#031f1b]">
        <video
          ref={videoRef}
          controls
          playsInline
          preload="none"
          poster={lessonPosterSrc}
          aria-label="Marisa Smutek singing during a lesson while Liuba Doga gives live coaching instruction"
          className="absolute inset-0 h-full w-full bg-[#031f1b] object-contain"
          onPlay={() => record("lesson_video_play")}
          onTimeUpdate={(event) => {
            const { currentTime, duration } = event.currentTarget;
            if (Number.isFinite(duration) && duration > 0 && currentTime / duration >= 0.5) record("lesson_video_halfway");
          }}
          onEnded={() => record("lesson_video_complete")}
        >
          <source src={lessonVideoSrc} type="video/mp4" />
          <track kind="captions" src="/videos/local-lessons/liuba-marisa-coaching-lesson.en.vtt" srcLang="en" label="English sound descriptions" />
        </video>
      {!isPlaying ? (
        <button
          type="button"
          data-amp-href={lessonVideoSrc}
          data-amp-poster={lessonPosterSrc}
          onClick={playVideo}
          className="group absolute inset-0 block h-full w-full overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d5a746]"
        >
          <Image
            src={lessonPosterSrc}
            alt=""
            fill
            quality={60}
            sizes="(min-width: 1024px) 48vw, 100vw"
            className="object-contain object-center transition duration-500 group-hover:scale-[1.015]"
            aria-hidden="true"
          />
          <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,31,27,0.06)_0%,rgba(3,31,27,0.18)_48%,rgba(3,31,27,0.72)_100%)]" aria-hidden="true" />
          <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#d5a746]/45 bg-[#fffaf2]/95 text-[#b27b21] shadow-[0_22px_54px_rgba(0,0,0,0.28)] transition group-hover:scale-105" aria-hidden="true">
            <svg viewBox="0 0 24 24" className="ml-1 h-9 w-9 fill-current">
              <path d="M8 5.5v13l10-6.5-10-6.5Z" />
            </svg>
          </span>
          <span className="absolute left-4 right-4 top-4 flex items-center justify-between gap-3">
            <span className="rounded-full border border-[#d5a746]/35 bg-[#062b25]/86 px-3 py-1.5 text-[0.68rem] font-black uppercase tracking-[0.2em] text-[#f2cf6b] shadow-[0_12px_28px_rgba(0,0,0,0.22)] backdrop-blur">
              Play lesson clip
            </span>
            <span className="rounded-full border border-white/20 bg-black/32 px-3 py-1.5 text-xs font-bold text-white/88 backdrop-blur">
              1:01
            </span>
          </span>
          <span className="absolute bottom-4 left-4 right-4 rounded-[14px] border border-white/16 bg-[#062b25]/88 p-4 text-white shadow-[0_18px_42px_rgba(0,0,0,0.22)] backdrop-blur">
            <span className="block text-sm font-black leading-5">Real 1:1 singing lesson with Liuba</span>
            <span className="mt-1 block text-sm font-semibold leading-5 text-white/76">
              Marisa Smutek sings while Liuba gives live coaching instruction.
            </span>
          </span>
        </button>
      ) : null}
      {isPlaying ? (
        <figcaption className="sr-only">
          Marisa Smutek singing while Liuba gives live 1-to-1 coaching instruction.
        </figcaption>
      ) : null}
    </figure>
  );
}
